import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import NotificationSystem from 'rc-notification';

import BasicNotification from 'components/Notification';
import {
  destroySequenceContact, updateSequenceContact,
  removeFromCampaignSequenceContact, removeBulkFromCampaignSequenceContact,
  updateBulkSequenceContact, destroyBulkSequenceContact,
} from 'redux/actions/sequenceContactActions';
import { NOTIFICATION_NODE_STYLE } from 'constants/misc';
import { updateSequence, getSequences } from 'redux/actions/sequenceActions';
import {
  createAccountBlacklistedContact, createBulkAccountBlacklistedContacts,
} from 'redux/actions/accountBlacklistedContactActions';
import { uuidv4 } from 'utils/misc';


const withContactList = (WrappedComponent) => {
  class ContactList extends Component {
    static propTypes = {
      sequences: PropTypes.shape({
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        count: PropTypes.number,
        next: PropTypes.string,
        previous: PropTypes.string,
        results: PropTypes.array,
      }).isRequired,
      contacts: PropTypes.shape({
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        results: PropTypes.array,
      }).isRequired,
      getContacts: PropTypes.func.isRequired,
    };

    constructor(props) {
      super(props);
      this.state = {
        filterParams: {
          search: null,
          statuses: null,
        },
        selectedIds: new Set(),
        lastFocusedRow: null,
        isSelectedAllContacts: false,
      };
      this.uuidv4 = uuidv4(); // preventing duplicated popover IDs when reusing component multiple times
      this.emitChangeDebounced = debounce(this.emitChange, 400);
      // selectedText will allow us to prevent toggling seqContact when user is trying to select text
      // we will check for difference in selectedText
      this.selectedText = window.getSelection().toString();
      this.getSelectedContactsOnPage = this.getSelectedContactsOnPage.bind(this);

      // will be used to hide all popovers (dirty hack)
      // it will allow us to open 'areYouSure' type modal and at the same time close all 'legacy' type popovers
      this.mainModalRef = React.createRef();
    }

    componentDidMount = () => {
      // eslint-disable-next-line no-return-assign
      NotificationSystem.newInstance({ style: { zIndex: '9999' } }, n => this.notification = n);
    }

    componentWillUnmount = () => {
      if (this.emitChangeDebounced) {
        this.emitChangeDebounced.cancel();
      }
      if (this.notification) {
        this.notification.destroy();
      }
    }

    onSelectedAllContacts = (value) => {
      this.setState({ isSelectedAllContacts: value });
    }

    getSelectedContactsOnPage = () => (
      this.props.contacts.results.filter(seqContact => this.state.selectedIds.has(seqContact.id))
    )

    handleSelectAll = () => {
      const numberOfSelectedContactsOnPage = this.getSelectedContactsOnPage().length;
      const selectedIdsCopy = new Set(this.state.selectedIds);
      // if we have at least one selected seqContact then we are isIndeterminate
      if (numberOfSelectedContactsOnPage > 0) {
        if (this.state.isSelectedAllContacts) {
          this.setState({ isSelectedAllContacts: false, selectedIds: new Set() });
        } else {
          // remove all on current page
          for (let i = 0; i < this.props.contacts.results.length; i += 1) {
            const seqContact = this.props.contacts.results[i];
            if (selectedIdsCopy.has(seqContact.id)) selectedIdsCopy.delete(seqContact.id);
          }
          this.setState({ selectedIds: selectedIdsCopy });
        }
      } else {
        // add all on the current page
        this.props.contacts.results.forEach(seqContact => selectedIdsCopy.add(seqContact.id));
        this.setState({ selectedIds: selectedIdsCopy });
      }
    };

    handleSelectClick = (evt, id) => {
      evt.stopPropagation();
      const { target } = evt;
      const { contacts } = this.props;
      const { lastFocusedRow, selectedIds } = this.state;
      const isUserTryingToSelectText = (
        window.getSelection().toString() !== this.selectedText && window.getSelection().toString()
      );
      if (!target.getAttribute('role') && !isUserTryingToSelectText) {
        // if element have a role attribute then it will handle onClick himself
        // if user is trying to select text we will ignore click also
        const selectedIdsCopy = new Set(selectedIds);
        const isFocusedContactOnPage = contacts.results.some(seqContact => seqContact.id === lastFocusedRow);
        if (evt.shiftKey && isFocusedContactOnPage && id !== lastFocusedRow) {
          // handle click + shift (select rows between last focused row and clicked row)
          // if clickedIndex is selected -> remove all newSelectedIds
          // if clickedIndex is not selected -> add all newSelectedIds
          const lastFocusedIndex = contacts.results.findIndex(obj => obj.id === lastFocusedRow);
          const clickedIndex = contacts.results.findIndex(obj => obj.id === id);
          if (lastFocusedIndex >= 0 && clickedIndex >= 0) {
            const newSelectedObjs = clickedIndex > lastFocusedIndex ?
              contacts.results.slice(lastFocusedIndex, clickedIndex + 1) :
              contacts.results.slice(clickedIndex, lastFocusedIndex + 1);
            const newSelectedIds = new Set(newSelectedObjs.map(seqContact => seqContact.id));
            const isClickedIndexSelected = selectedIds.has(id);
            if (isClickedIndexSelected) {
              newSelectedIds.forEach(seqContactId => selectedIdsCopy.delete(seqContactId));
            } else {
              newSelectedIds.forEach(seqContactId => selectedIdsCopy.add(seqContactId));
            }
          }
        } else if (selectedIds.has(id)) {
          selectedIdsCopy.delete(id);
        } else {
          selectedIdsCopy.add(id);
        }
        this.setState({ selectedIds: selectedIdsCopy, lastFocusedRow: id });
      } else {
        this.selectedText = window.getSelection().toString();
      }
    };

    handleChangeFilter = (filters) => {
      const { filterParams } = this.state;
      const updatedFilterParams = {
        ...filterParams,
        statuses: filters.map(values => (values.value)).join(', '),
      };
      this.props.getContacts(updatedFilterParams);
      this.setState({ filterParams: updatedFilterParams });
    };

    emitChange = (value) => {
      const updatedFilterParams = {
        ...this.state.filterParams,
        search: value,
      };
      this.props.getContacts(updatedFilterParams);
      this.setState({ filterParams: updatedFilterParams });
    }

    showNotification = (text, color) => {
      this.notification.notice({
        content: <BasicNotification
          color={color}
          message={text}
        />,
        duration: 4,
        closable: false,
        style: NOTIFICATION_NODE_STYLE,
        className: 'alert--colored right-up',
      });
    }

    untoggleSelectedContactOnPage = () => {
      const selectedContactsOnPage = this.getSelectedContactsOnPage();
      const selectedIdsCopy = new Set(this.state.selectedIds);
      for (let i = 0; i < selectedContactsOnPage.length; i += 1) {
        const seqContact = selectedContactsOnPage[i];
        if (selectedIdsCopy.has(seqContact.id)) selectedIdsCopy.delete(seqContact.id);
      }
      this.setState({ selectedIds: selectedIdsCopy });
    }

    isSelected = id => this.state.selectedIds.has(id);

    isAllChecked = () => {
      const numberOfSelectedContactsOnPage = this.getSelectedContactsOnPage().length;
      return numberOfSelectedContactsOnPage > 0 &&
        numberOfSelectedContactsOnPage === this.props.contacts.results.length;
    }

    isIndeterminate = () => {
      const numberOfSelectedContactsOnPage = this.getSelectedContactsOnPage().length;
      return numberOfSelectedContactsOnPage > 0 &&
        numberOfSelectedContactsOnPage < this.props.contacts.results.length;
    }

    removeSelection = () => {
      this.setState({ selectedIds: new Set(), isSelectedAllContacts: false });
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          isIndeterminate={this.isIndeterminate}
          isAllChecked={this.isAllChecked}
          isSelected={this.isSelected}
          untoggleSelectedContactOnPage={this.untoggleSelectedContactOnPage}
          showNotification={this.showNotification}
          emitChangeDebounced={this.emitChangeDebounced}
          handleChangeFilter={this.handleChangeFilter}
          handleSelectClick={this.handleSelectClick}
          handleSelectAll={this.handleSelectAll}
          getSelectedContactsOnPage={this.getSelectedContactsOnPage}
          onSelectedAllContacts={this.onSelectedAllContacts}
          isSelectedAllContacts={this.state.isSelectedAllContacts}
          removeSelection={this.removeSelection}
        />
      );
    }
  }
  return compose(connect(
    state => ({
      sequences: state.sequences,
    }),
    {
      getSequences,
      updateSequence,
      updateSequenceContact,
      destroySequenceContact,
      updateBulkSequenceContact,
      destroyBulkSequenceContact,
      createAccountBlacklistedContact,
      removeFromCampaignSequenceContact,
      createBulkAccountBlacklistedContacts,
      removeBulkFromCampaignSequenceContact,
    },
  ))(ContactList);
};

export default withContactList;
